/**
 * Pending Review column chooser: a "Columns" button with a menu of the six
 * rule columns, persisted in localStorage. Tags and Brand start hidden.
 * Name/SKU/Actions are never choosable.
 */
import React, { useCallback, useState } from 'react';
import { Button, Checkbox, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { STAGED_ITEM_RULE_HEADERS } from './StagedItemRuleCells';

export const PENDING_REVIEW_COLUMNS_KEY = 'vaperite.pendingReview.columns';
export const DEFAULT_VISIBLE_COLUMNS: readonly string[] = ['Vend type', 'Category', 'Sell in app', '18+'];

const load = (): Set<string> => {
  try {
    const raw = localStorage.getItem(PENDING_REVIEW_COLUMNS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((c): c is string => (STAGED_ITEM_RULE_HEADERS as readonly string[]).includes(c)));
      }
    }
  } catch {
    // unreadable storage: fall back to the defaults
  }
  return new Set(DEFAULT_VISIBLE_COLUMNS);
};

export interface PendingReviewColumnsState {
  visible: Set<string>;
  toggle: (column: string) => void;
}

export function usePendingReviewColumns(): PendingReviewColumnsState {
  const [visible, setVisible] = useState<Set<string>>(load);
  const toggle = useCallback((column: string) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(column)) next.delete(column); else next.add(column);
      try {
        localStorage.setItem(PENDING_REVIEW_COLUMNS_KEY, JSON.stringify(STAGED_ITEM_RULE_HEADERS.filter((h) => next.has(h))));
      } catch {
        // storage blocked: the choice lasts for this page view only
      }
      return next;
    });
  }, []);
  return { visible, toggle };
}

const PendingReviewColumnsButton: React.FC<{ columns: PendingReviewColumnsState }> = ({ columns }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ViewColumnIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ fontSize: '0.72rem', fontWeight: 600 }}
      >
        Columns
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {STAGED_ITEM_RULE_HEADERS.map((h) => (
          <MenuItem key={h} dense onClick={() => columns.toggle(h)}>
            <ListItemIcon>
              <Checkbox edge="start" size="small" checked={columns.visible.has(h)} tabIndex={-1} disableRipple />
            </ListItemIcon>
            <ListItemText primary={h} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PendingReviewColumnsButton;
